import { apiMessage } from "rc-app/utils/guidedRoomFlow";

/* ── Guided room: recording, as the host's dock sees it ────────────────
   Pure decisions only, so the host page stays wiring and these stay
   testable. The backend is re-admin-server `recording_routers.py`:

     POST /actions/guided_room/{room}/recording/start   host only
     POST /actions/guided_room/{room}/recording/stop    host only
     GET  /views/guided_room/{room}/recordings           newest first

   WHY THE HOST HAS A BUTTON AT ALL
   Recording auto-starts only when a SCHEDULED room is started. Every tour
   the project page creates is INSTANT — born LIVE — so it never reached
   auto_start, and until this button existed not one of those tours could
   be recorded (found end-to-end, 2026-09-11). */

export type RecordingState =
  | "STARTING"     // asked Agora, no sid yet
  | "RECORDING"    // running
  | "STOPPING"     // stop issued, files may still be uploading
  | "STORED"       // audio in the bucket, not analysed yet
  | "TRANSCRIBED"  // words exist
  | "ANALYSED"     // words + judgement exist
  | "FAILED";      // gave up; `error` says why

export type RecordingBrief = {
  recording_id: string;
  state?: string | null;
  error?: string | null;
  started_at?: string;
  stopped_at?: string;
  duration_seconds?: number | null;
  sentiment?: string | null;
};

const norm = (s: unknown) => String(s || "").trim().toUpperCase();

/* The backend's reason for an unrecordable channel names an env var and
   Agora internals. True, and useless to an agent mid-tour — and it arrives
   by TWO routes: the 409 on pressing Record, and the FAILED row's `error`
   that the poll reads back after a reload. Both go through this. */
/* Narrow on purpose: any other failure that merely mentions a uid is a
   different problem and the host should see its real text. */
const UID_RULE = /GUIDED_RTC_UID_MODE|string uids?|integer uid/i;
const UID_TEXT = "This tour can't be recorded yet — a server setting still has to be switched on.";

/* ── A START THAT NEVER FINISHED ──
   The backend writes STARTING, then talks to Agora, then writes RECORDING
   or FAILED. On stage on 2026-09-11 the Agora call crashed on a dependency
   mismatch that start() doesn't catch, so the row sat on STARTING for good
   while the dock said "Recording is starting — press to stop" — and Stop
   500'd too. A real start takes seconds; past this, say what happened. */
export const STALE_START_MS = 90_000;

/** Backend timestamps are naive server-UTC strings ("2026-09-11 10:34:05.65"). */
export const parseServerTime = (value: unknown): number | null => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const iso = raw.replace(" ", "T");
  const ms = Date.parse(/[zZ]$|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  return Number.isFinite(ms) ? ms : null;
};

export const isStaleStart = (latest: RecordingBrief | null, now: number): boolean => {
  if (norm(latest?.state) !== "STARTING") return false;
  const at = parseServerTime(latest?.started_at);
  return at !== null && now - at > STALE_START_MS;
};

/** STARTING counts: a stop is meaningful the moment Agora has been asked. */
export const isRecordingActive = (state: unknown): boolean =>
  norm(state) === "RECORDING" || norm(state) === "STARTING";

/** The backend already sorts newest first; anything else is "nothing yet". */
export const latestRecording = (rows: unknown): RecordingBrief | null =>
  (Array.isArray(rows) && rows.length && rows[0] && typeof rows[0] === "object"
    ? (rows[0] as RecordingBrief) : null);

export type RecordingDock = {
  action: "start" | "stop" | null;
  label: string;       // aria-label — says what pressing it does
  tip: string;         // hover text — says what is true now
  pressed: boolean;
  disabled: boolean;
  recording: boolean;  // drives the REC pill in the header
};

export const recordingDock = (o: {
  live: boolean;
  enabled: boolean;
  latest: RecordingBrief | null;
  busy: "start" | "stop" | null;
  problem?: string;
  now?: number;
}): RecordingDock => {
  const state = norm(o.latest?.state);
  /* Checked before "running": a start that never finished is not a
     recording, and offering Stop on it only produces a second error. */
  if (!o.busy && isStaleStart(o.latest, o.now ?? Date.now())) {
    return {
      action: null,
      disabled: true,
      pressed: false,
      recording: false,
      label: "Recording didn't start",
      tip: "Recording didn't start — something went wrong on the server. The tour itself is fine.",
    };
  }
  const running = isRecordingActive(state);
  const base = { pressed: running, recording: state === "RECORDING" };

  if (o.busy === "start") {
    return { ...base, action: null, disabled: true, label: "Starting recording", tip: "Starting recording…" };
  }
  if (o.busy === "stop") {
    return { ...base, action: null, disabled: true, label: "Stopping recording", tip: "Stopping recording…" };
  }
  /* A running recording can always be stopped — even if the room just
     stopped reading as live, leaving a recorder on is the worse mistake. */
  if (running) {
    return {
      ...base,
      action: "stop",
      disabled: false,
      label: "Stop recording",
      tip: state === "STARTING" ? "Recording is starting — press to stop" : "Recording — press to stop",
    };
  }
  if (state === "STOPPING") {
    return { ...base, action: null, disabled: true, label: "Saving recording", tip: "Saving the recording…" };
  }
  if (!o.live) {
    return { ...base, action: null, disabled: true, label: "Start recording", tip: "Recording is available once the tour is live" };
  }
  if (!o.enabled) {
    return { ...base, action: null, disabled: true, label: "Start recording", tip: "Recording is switched off for this tour" };
  }
  const tip = o.problem
    || (state === "FAILED"
      ? (UID_RULE.test(String(o.latest?.error || ""))
        ? UID_TEXT
        /* The row carries whatever the server caught — on stage, 2026-09-11,
           'Agora acquire -> 400: {"reason":"invalid_appid"}'. True, and not
           words for an agent mid-tour; the full text stays on the recording
           row for whoever fixes it. */
        : "The last recording didn't start — something went wrong on the server. Press to try again")
      : ["STORED", "TRANSCRIBED", "ANALYSED"].includes(state)
        ? "Saved. Press to record again"
        : "Start recording");
  return { ...base, action: "start", disabled: false, label: "Start recording", tip };
};

/** What to tell a HOST when start/stop is refused — words for an agent,
    not a stack trace. The raw backend text still reaches the console. */
export const recordingProblem = (e: any): string => {
  const status = e?.response?.status;
  const said = apiMessage(e, "");
  if (status === 409) {
    return UID_RULE.test(said) ? UID_TEXT : said || "This tour can't be recorded as it's set up.";
  }
  if (status === 503) return "Recording isn't set up on this server yet.";
  if (status === 404) return "No recording is running.";
  if (status === 401 || status === 403) return "Only the host can record this tour.";
  if (status === 400) return said || "Recording can't start right now.";
  /* A 5xx body is a Python exception ("AsyncConnectionPool.__init__() got an
     unexpected keyword argument…" on stage, 2026-09-11). Never put that on a
     host's button. */
  if (typeof status === "number" && status >= 500) {
    return "Recording didn't start — something went wrong on the server. The tour itself is fine.";
  }
  return said || "Couldn't reach the recording service. Try again.";
};

/* ── "Let new people straight in" ─────────────────────────────────────
   Founder decision 2026-09-11: new leads wait in the lobby by default (the
   lead is created when they verify, before admission — so nothing is lost
   by waiting), with a switch INSIDE the room for open tours. Backend item
   32 shipped it as `settings.open_admission` (Nivedita, fa0380e): verify_otp
   admits when invited OR open_admission, and the lead is still created
   first. The switch only appears once a room actually carries that key,
   so it can never promise a behaviour the server doesn't have. */
export const AUTO_ADMIT_KEY = "open_admission";

export const autoAdmitSetting = (room: any): { available: boolean; on: boolean } => {
  const s = room?.settings;
  const available = !!s && typeof s === "object" && Object.prototype.hasOwnProperty.call(s, AUTO_ADMIT_KEY);
  return { available, on: available && s[AUTO_ADMIT_KEY] === true };
};
